import {useState} from 'react';
import {Button, Form, Select, Switch} from 'antd';
import {ArrowLeftOutlined, ExclamationCircleOutlined} from '@ant-design/icons';
import {useNavigate} from 'react-router-dom';
import {useLazyQuery, useMutation} from '@apollo/client';
import {toast} from 'react-toastify';

import DBModal from 'components/modal/modal';
import {UPDATE_PAYMENT_PROVIDER_SETTINGS} from 'graphql/mutations/wallet';
import {GET_BOTH_PLATFORM_AND_WALLET_CURRENCY} from 'graphql/queries/wallet';
import {walletCurrencies} from 'utils/mock';
import {prettifyEnum} from 'utils/currencyFormatting';

import DepositWalletFields from '../view/WalletFields/DepositWalletField';
import WithdrawalWalletFields from '../view/WalletFields/WithdrawalWalletFields';
import {handleUpdatePaymentProviderSetting} from '../actions/MutationRequests';
import styles from './createProviderSettings.module.scss';

/**
 * The full PaymentGatewayProviderTypes enum, taken from the schema rather than
 * hand-listed. The previous eight-item list left out HUB2, INTERAC and
 * MAVIANCE — all three already carry live balances — so those providers simply
 * could not be configured from this screen.
 */
export const PAYMENT_PROVIDERS = [
  'PLATFORM',
  'DABA',
  'FLUTTERWAVE',
  'PAYSTACK',
  'PAYPAL',
  'DWOLLA',
  'HUB2',
  'MAVIANCE',
  'WISE',
  'REVOLUT',
  'INTERAC',
  'SVB',
  'BANK_ACCOUNT',
  'APPLE',
  'GOOGLE_PAY',
];

const providerOptions = PAYMENT_PROVIDERS.map(value => ({
  value,
  label: prettifyEnum(value),
}));

const currencyOptions = walletCurrencies.map(item => ({
  value: item.key,
  label: `${item.key} — ${item.name}`,
}));

/**
 * True when a fixed fee would swallow the whole minimum transaction.
 *
 * Only FIXED fees are comparable to an amount — a VARIABLE fee is a
 * percentage, so checking "3 >= 10" against it is meaningless and could reject
 * a perfectly valid 50% fee on a minimum of 20. A zero fixed fee is also fine;
 * the previous version rejected it because it tested truthiness rather than
 * magnitude, so `(0 < amount && 0)` came out falsy.
 */
export const checkFeeValues = (fees, amount) => {
  const minimum = Number(amount);
  if (!fees?.length || !Number.isFinite(minimum) || minimum <= 0) return false;
  return fees.some(fee => {
    if (fee?.feeType !== 'FIXED') return false;
    const fixed = Number(fee?.fixedFee);
    return Number.isFinite(fixed) && fixed >= minimum;
  });
};

const CreateProviderSettings = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();

  // Provider, currency and the provider toggle live outside the form on
  // purpose: DepositWalletFields calls form.resetFields() whenever the currency
  // changes, which would otherwise wipe the two selects that drive this page.
  const [provider, setProvider] = useState();
  const [selectedCurrency, setSelectedCurrency] = useState();
  const [enableProvider, setEnableProvider] = useState(true);
  const [touched, setTouched] = useState(false);

  const [confirming, setConfirming] = useState(false);
  const [pendingValues, setPendingValues] = useState(null);

  const [updatePaymentProviderCurrencySettings, {loading}] = useMutation(
    UPDATE_PAYMENT_PROVIDER_SETTINGS,
  );
  const [getPaymentProviderByProviderName, {data}] = useLazyQuery(
    GET_BOTH_PLATFORM_AND_WALLET_CURRENCY,
  );

  const readyForFields = !!provider && !!selectedCurrency;

  const handleCurrencyChange = value => {
    setSelectedCurrency(value);
    if (provider && value) {
      getPaymentProviderByProviderName({
        variables: {providerName: provider, currency2: value},
      });
    }
  };

  const handleProviderChange = value => {
    setProvider(value);
    if (value && selectedCurrency) {
      getPaymentProviderByProviderName({
        variables: {providerName: value, currency2: selectedCurrency},
      });
    }
  };

  const handleCreate = values => {
    setTouched(true);
    if (!provider || !selectedCurrency) {
      toast.error('Choose a payment provider and a currency first');
      return;
    }

    // The old call passed the form instance here instead of the withdrawal fee
    // list, so `fees?.length` was undefined and withdrawal fees were never
    // checked against the minimum at all.
    if (checkFeeValues(values?.withdrawalFees, values?.minWithdrawalAmount)) {
      toast.error(
        'Withdrawal fees must be less than the minimum withdrawal amount',
      );
      return;
    }
    if (checkFeeValues(values?.depositFees, values?.minDepositAmount)) {
      toast.error('Deposit fees must be less than the minimum deposit amount');
      return;
    }

    setPendingValues(values);
    setConfirming(true);
  };

  /** Without this the button silently does nothing when a field off-screen fails. */
  const handleCreateFailed = ({errorFields}) => {
    setTouched(true);
    toast.error(
      errorFields?.[0]?.errors?.[0] ??
        'Please fill in all the required fields before creating',
    );
  };

  const handleConfirm = () =>
    handleUpdatePaymentProviderSetting(
      updatePaymentProviderCurrencySettings,
      pendingValues,
      provider,
      enableProvider,
      selectedCurrency,
      pendingValues?.depositFees || [],
      pendingValues?.withdrawalFees || [],
      pendingValues?.depositDiscount || [],
      pendingValues?.withdrawalDiscount || [],
      () => {
        setConfirming(false);
        navigate('/dashboard/wallet');
      },
    );

  const confirmRows = [
    {label: 'Provider', value: prettifyEnum(provider)},
    {label: 'Currency', value: selectedCurrency},
    {label: 'Provider enabled', value: enableProvider ? 'On' : 'Off'},
    {
      label: 'Deposits enabled',
      value: pendingValues?.allowedDeposit ? 'On' : 'Off',
    },
    {
      label: 'Withdrawals enabled',
      value: pendingValues?.allowedWithdrawal ? 'On' : 'Off',
    },
    {
      label: 'Deposit fees',
      value: `${pendingValues?.depositFees?.length ?? 0} configured`,
    },
    {
      label: 'Withdrawal fees',
      value: `${pendingValues?.withdrawalFees?.length ?? 0} configured`,
    },
  ];

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <button
          type="button"
          className={styles.backButton}
          onClick={() => navigate('/dashboard/wallet')}>
          <ArrowLeftOutlined /> Wallet
        </button>
        <h1 className={styles.title}>Provider settings</h1>
        <p className={styles.subtitle}>
          Set how one payment provider handles a single currency — the limits,
          the fees and whether money can move through it at all.
        </p>
      </div>

      <section className={styles.card}>
        <div className={styles.cardHeader}>
          <h2 className={styles.cardTitle}>Provider and currency</h2>
          <p className={styles.cardHint}>
            Pick both to load the platform limits this pair has to sit inside.
          </p>
        </div>

        <div className={styles.fieldsRow}>
          <div className={styles.field}>
            <span className={`${styles.label} ${styles.required}`}>
              Payment provider
            </span>
            <Select
              showSearch
              optionFilterProp="label"
              className={styles.fullWidth}
              style={{width: '100%'}}
              placeholder="Select a payment provider"
              value={provider}
              options={providerOptions}
              onChange={handleProviderChange}
            />
            {touched && !provider && (
              <div className={styles.fieldError}>Select a payment provider</div>
            )}
          </div>

          <div className={styles.field}>
            <span className={`${styles.label} ${styles.required}`}>
              Currency
            </span>
            <Select
              showSearch
              optionFilterProp="label"
              style={{width: '100%'}}
              placeholder="Select a currency"
              value={selectedCurrency}
              options={currencyOptions}
              onChange={handleCurrencyChange}
            />
            {touched && !selectedCurrency && (
              <div className={styles.fieldError}>Select a currency</div>
            )}
          </div>

          <div className={styles.field}>
            <span className={styles.label}>Provider enabled</span>
            {/* Deliberately not a Form.Item. The old one was registered as
                `allowedDeposit`, the same name DepositWalletFields uses for the
                deposit toggle, so the two fought over one value. */}
            <Switch
              className={styles.switch}
              checked={enableProvider}
              onChange={setEnableProvider}
            />
          </div>
        </div>
      </section>

      {readyForFields ? (
        <Form
          form={form}
          layout="vertical"
          scrollToFirstError
          onFinish={handleCreate}
          onFinishFailed={handleCreateFailed}>
          <DepositWalletFields
            selectedCurrency={selectedCurrency}
            form={form}
            // This screen owns provider enablement above. The toggle inside
            // DepositWalletFields writes to `enableProvider`, which nothing
            // reads, so showing it here offers a control that does nothing.
            showEnableToggle={false}
            currencyLimits={
              data?.getSupportedCurrencyByCurrencyName?.deposit?.limit
            }
            currencyDetails={{}}
          />
          <WithdrawalWalletFields
            selectedCurrency={selectedCurrency}
            loading={loading}
            // This screen has its own Cancel / Review footer below; without
            // this the shared component adds a second submit button labelled
            // "Create USD Currency" on a provider-settings page.
            showSubmitButton={false}
            currencyLimits={
              data?.getSupportedCurrencyByCurrencyName?.withdrawal?.limit
            }
            currencyDetails={{}}
            form={form}
          />
          <div className={styles.footer}>
            <Button onClick={() => navigate('/dashboard/wallet')}>
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              className={styles.primaryButton}>
              Review and create
            </Button>
          </div>
        </Form>
      ) : (
        <div className={styles.emptyHint}>
          <h3>Choose a provider and a currency</h3>
          <p>
            The deposit and withdrawal settings appear once both are selected.
          </p>
        </div>
      )}

      <DBModal
        isOpen={confirming}
        handleClose={() => setConfirming(false)}
        width={560}
        content={
          <div className={styles.confirmModal}>
            <div>
              <h1 className={styles.confirmTitle}>
                Create {selectedCurrency} settings for {prettifyEnum(provider)}?
              </h1>
              <p className={styles.confirmSubtitle}>
                This becomes the live deposit and withdrawal rule every user
                sees for this pair.
              </p>
            </div>

            <div className={styles.confirmList}>
              {confirmRows.map(row => (
                <div className={styles.confirmRow} key={row.label}>
                  <span className={styles.confirmLabel}>{row.label}</span>
                  <span className={styles.confirmValue}>{row.value}</span>
                </div>
              ))}
            </div>

            {!enableProvider && (
              <div className={styles.confirmWarning}>
                <ExclamationCircleOutlined />
                <span>
                  The provider is turned off, so these rules will be saved but
                  no money will move through it.
                </span>
              </div>
            )}

            <div className={styles.confirmFooter}>
              <Button onClick={() => setConfirming(false)}>Back</Button>
              <Button
                type="primary"
                className={styles.primaryButton}
                loading={loading}
                onClick={handleConfirm}>
                Create settings
              </Button>
            </div>
          </div>
        }
      />
    </div>
  );
};

export default CreateProviderSettings;
